'use client';

import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface IModalRef {
    /**
     * 打开弹窗
     * @param payload 业务自定义数据；默认 Modal 不渲染，仅保存于 ref 便于扩展
     */
    open: (payload?: unknown) => void;
    /** 关闭弹窗 */
    close: () => void;
    /** 当前是否展示 */
    showing: boolean;
}

export interface IModalProps {
    /** 弹窗主体内容 */
    children?: React.ReactNode;
    /** 是否显示蒙层，默认 true */
    enableMask?: boolean;
    /** 点击蒙层是否可关闭，默认 true */
    maskClosable?: boolean;
    /** 关闭完成后的回调 */
    onClose?: () => void;
    /**
     * 主体位置与进入动效，默认 bottom（底部滑入，与历史行为一致）
     * - bottom：底部抽屉
     * - center：屏幕居中，缩放 + 淡入弹出
     */
    placement?: 'bottom' | 'center';
}

// ─── Animation duration (ms) — keep in sync with transition duration below ───
const ANIMATION_DURATION = 300;

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Modal 组件：默认底部抽屉；可选 `placement="center"` 为居中缩放弹出。
 *
 * 用法：
 * ```tsx
 * const modalRef = useRef<IModalRef>(null);
 *
 * <Modal ref={modalRef} enableMask maskClosable onClose={() => {}}>
 *   <div>弹窗内容</div>
 * </Modal>
 *
 * <Modal ref={modalRef} placement="center" onClose={() => {}}>
 *   <div>居中弹出</div>
 * </Modal>
 *
 * // 打开
 * modalRef.current?.open();
 * // 关闭
 * modalRef.current?.close();
 * ```
 */

const MASK_OPACITY = 0.7;

const Modal = forwardRef<IModalRef, IModalProps>((props, ref) => {
    const { children, enableMask = true, maskClosable = true, onClose, placement = 'bottom' } = props;
    const isCenter = placement === 'center';

    // mounted: DOM 是否挂载
    const [mounted, setMounted] = useState(false);
    // visible: 触发 CSS 进入/退出动画
    const [visible, setVisible] = useState(false);
    // clientReady: 客户端是否已挂载，避免 SSR 阶段执行 createPortal
    const [clientReady, setClientReady] = useState(false);
    const lastOpenPayloadRef = useRef<unknown>(undefined);

    // 关闭动画 timer，防止快速开关时出现残留
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const close = useCallback(
        (e?: React.MouseEvent) => {
            e?.stopPropagation();
            setVisible(false);
            closeTimerRef.current = setTimeout(() => {
                setMounted(false);
                onClose?.();
            }, ANIMATION_DURATION);
        },
        [onClose]
    );

    const open = useCallback((payload?: unknown) => {
        lastOpenPayloadRef.current = payload;
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
        setMounted(true);
        // 双 rAF 确保 DOM 挂载后再触发进入动画
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setVisible(true);
            });
        });
    }, []);

    useImperativeHandle(ref, () => ({ open, close, showing: mounted }), [open, close, mounted]);

    useEffect(() => {
        setClientReady(true);
    }, []);

    if (!mounted || !clientReady) {
        return null;
    }

    const rootClass = `fixed inset-0 z-[49] flex ${enableMask ? '' : 'pointer-events-none'} ${
        isCenter
            ? 'items-center justify-center pt-4 pb-[max(1rem,env(safe-area-inset-bottom,0px))]'
            : 'flex-col justify-end'
    }`;

    const panelClass = `pointer-events-auto relative w-full ${
        isCenter ? 'duration-300 ease-out' : 'transition-transform duration-300'
    }`;

    const panelStyle = isCenter
        ? {
              transform: visible ? 'scale(1) translateY(0)' : 'scale(0.92) translateY(16px)',
              opacity: visible ? 1 : 0,
          }
        : { transform: visible ? 'translateY(0)' : 'translateY(100%)' };

    return createPortal(
        <div className={rootClass}>
            {/* 蒙层 */}
            {enableMask && (
                <div
                    className='absolute inset-0 bg-black transition-opacity duration-300'
                    style={{ opacity: visible ? MASK_OPACITY : 0 }}
                    onClick={maskClosable ? close : undefined}
                />
            )}

            {/* 弹窗主体：bottom 自底部滑入；center 自中间缩放弹出 */}
            <div className={panelClass} style={panelStyle}>
                <div
                    className={isCenter ? 'flex items-center justify-center' : 'transition-opacity duration-300'}
                    style={isCenter ? undefined : { opacity: visible ? 1 : 0 }}
                >
                    {children}
                </div>
                {!isCenter && <div className='h-[env(safe-area-inset-bottom,0px)]' />}
            </div>
        </div>,
        document.body
    );
});

Modal.displayName = 'Modal';

export default Modal;
